/**
 * # Mobile Menu Context
 * - Provides context of mobile navigation status to consuming components.
 * This is especially helpful when the navigation contains anchors, which
 * don't produce a page reload and thereby would keep the navigation open.
 */

/**
 * ## Imports
 */
import React, { createContext, useState, useContext, useMemo, ReactNode } from 'react';

/**
 * ## Types / Interfaces
 */
type MobileMenuContextType = {
    isMobileMenuOpen: boolean;
    toggleMobileMenu: () => void;
    closeMobileMenu: () => void;
}

/**
 * ## Context
 */
const MobileMenuContext = createContext<MobileMenuContextType | undefined>(undefined);

/**
 * ## Provider
 * Provides a context for managing the state of the mobile menu and exposes
 * a `MobileMenuProvider` component and `useMobileMenu` hook for use in child components.
 */
export const MobileMenuProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const contextValue = useMemo(() => {
        return {
            isMobileMenuOpen,
            toggleMobileMenu: () => {
                setIsMobileMenuOpen((prev) => { return !prev; });
            },
            closeMobileMenu: () => {
                setIsMobileMenuOpen(false);
            },
        };
    }, [isMobileMenuOpen]);

    return (
        <MobileMenuContext.Provider value={contextValue}>
            {children}
        </MobileMenuContext.Provider>
    );
};

export const useMobileMenu = (): MobileMenuContextType => {
    const context = useContext(MobileMenuContext);

    if (!context) {
        throw new Error('useMobileMenu must be used within a MobileMenuProvider!');
    }

    return context;
};
